import { Encounter } from '@hb/shared'

// §1.6.1 "Dual Timestamp — Every Entry, Every Note". Note-level only (decision 6 —
// per-section/per-entry granularity is an outstanding ambiguity). Pure display logic;
// name resolution (uid → display name) happens in the component via useCachedUser.
//
// Identity source is `createdBy`/`updatedBy` (DBMetadata), NOT `noteAuthor`:
//   - symmetric — both Created and Last Modified identities resolve the same way (uid →
//     useCachedUser), so "same vs different editor" is a clean uid comparison
//   - always present — createdBy/updatedBy are required metadata; noteAuthor is optional
//     and only captured for notes created through the form path (null on legacy docs)
//   - they denote the same person — noteAuthor is captured at first save, i.e. the creator
export interface EncounterTimestampDisplay {
  createdOn: number
  createdBy: string
  /**
   * null when the note was never edited after creation (createdOn === updatedOn — addMetadata
   * writes both from a single Date.now() on first save). Collapse to the Created part only.
   */
  lastModified: { updatedOn: number; updatedBy: string } | null
  /** true only when the note was edited AND the last editor differs from the creator (spec: show both identities) */
  differentEditor: boolean
}

export const getEncounterTimestampDisplay = (
  encounter: Pick<Encounter, 'createdOn' | 'createdBy' | 'updatedOn' | 'updatedBy'>,
): EncounterTimestampDisplay => {
  const { createdOn, createdBy, updatedOn, updatedBy } = encounter
  const edited = updatedOn !== createdOn
  return {
    createdOn,
    createdBy,
    lastModified: edited ? { updatedOn, updatedBy } : null,
    differentEditor: edited && updatedBy !== createdBy,
  }
}
